import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, Modal, Linking, SafeAreaView, Dimensions } from 'react-native';

import AppTextInput from '../components/AppTextInput';
const { width: WINDOW_WIDTH } = Dimensions.get('window');
import { AuthManager, User } from '../utils/authManager';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import { UserTrialManager, UserTrialData } from '../utils/userTrialManager';
import UserBottomMenu from '../components/UserBottomMenu';
import DeleteAccountConfirmModal from '../components/DeleteAccountConfirmModal';
import { COUNTRIES, getStatesForCountry } from '../constants/states';
import { geocodingService } from '../services/geocodingService';
import MyVehiclesModal from '../components/MyVehiclesModal';

// RN Web: window exists, but TS may not include DOM lib types
declare const window: any;

interface UserProfileScreenProps {
  onBack: () => void;
  onLogout: () => void;
  username?: string;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile') => void;
  onNavigateToChat?: () => void;
  onNavigateToSubscription?: () => void;
}

const UserProfileScreen: React.FC<UserProfileScreenProps> = ({
  onBack,
  onLogout,
  username,
  onTabPress,
  onNavigateToChat,
  onNavigateToSubscription,
}) => {
  // UnifiedAuthService user shape is not identical to AuthManager.User
  const [user, setUser] = useState<any | null>(null);
  const [currentTrial, setCurrentTrial] = useState<UserTrialData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '', // Street address
    city: '',
    state: '',
    zipcode: '',
    country: 'USA',
  });

  // Dropdown states
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    notifications: true,
    emailUpdates: false,
    smsReminders: false,
    locationServices: false,
  });

  // Inline validation error for zipcode (shown on web and mobile)
  const [zipcodeError, setZipcodeError] = useState<string | null>(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showVehiclesModal, setShowVehiclesModal] = useState(false);

  useEffect(() => {
    loadUserData();
    loadTrialData();
  }, []);

  const loadUserData = async () => {
    try {
      // Use UnifiedAuthService for both web and mobile to ensure we read from Firestore
      // UnifiedAuthService correctly handles both platforms and reads from Firestore
      const userData: any | null = await UnifiedAuthService.getCurrentUser();
      
      setUser(userData);
      if (userData) {
        // Handle address - can be string or object
        let streetAddress = '';
        let city = '';
        let state = '';
        let zipcode = '';
        let country = 'USA';
        
        if (userData.address) {
          if (typeof userData.address === 'string') {
            // If address is a string, try to parse it or use as street
            streetAddress = userData.address;
          } else if (typeof userData.address === 'object') {
            // Extract separate address fields from object
            const addr = userData.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
            streetAddress = addr.street || '';
            city = addr.city || '';
            state = addr.state || '';
            zipcode = addr.zipcode || '';
            country = addr.country || 'USA';
          }
        }
        
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: streetAddress,
          city: city,
          state: state,
          zipcode: zipcode,
          country: country,
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrialData = async () => {
    try {
      const trialData = await UserTrialManager.getTrialData();
      setCurrentTrial(trialData);
    } catch (error) {
      console.error('Error loading trial data:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setZipcodeError(null);
    const zipcodeTrimmed = (formData.zipcode || '').trim();
    if (!zipcodeTrimmed) {
      const msg = 'Zipcode is required.';
      setZipcodeError(msg);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(`Invalid Zipcode\n\n${msg}`);
      } else {
        Alert.alert('Invalid Zipcode', msg);
      }
      return;
    }
    if (!geocodingService.isValidZipcode(zipcodeTrimmed, formData.country)) {
      const msg =
        'Enter a valid US ZIP (5 digits), Canadian postal code (e.g. A1A 1A1), or India PIN (6 digits).';
      setZipcodeError(msg);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(`Invalid Zipcode\n\n${msg}`);
      } else {
        Alert.alert('Invalid Zipcode', msg);
      }
      return;
    }
    const normalizedZipcode = geocodingService.normalizeZipcodeForStorage(
      zipcodeTrimmed,
      formData.country
    );

    try {
      // Save address as object with separate fields (zipcode normalized to US 5-digit or Canada A1A 1A1)
      const addressObject = {
        street: formData.address,
        city: formData.city,
        state: formData.state,
        zipcode: normalizedZipcode,
        country: formData.country,
      };

      const updatedUser = {
        ...user,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: addressObject,
      };
      
      // Save to Firebase (web) or AuthManager (mobile)
      if (Platform.OS === 'web') {
        // Update Firestore document
        const { doc, updateDoc } = require('firebase/firestore');
        const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
        
        const initialized = initializeFirebase();
        if (!initialized) {
          console.warn('⚠️  UserProfile: Firebase not initialized, skipping Firestore update');
        } else {
          const db = getWebDb();
          if (!db) {
            console.warn('⚠️  UserProfile: No Firebase database instance found, skipping Firestore update');
          } else if (user.id) {
            await updateDoc(doc(db, 'users', user.id), {
              name: updatedUser.name,
              email: updatedUser.email,
              phone: updatedUser.phone,
              address: addressObject,
            });
            console.log('✅ UserProfile: Profile updated in Firestore');
          }
        }
        // Sync to local storage so getCurrentUser() returns fresh data when we reload
        try {
          const ProductionStorage = (await import('../utils/productionStorage')).default;
          await ProductionStorage.setItem('user', JSON.stringify({ ...updatedUser, lastLogin: new Date().toISOString() }));
        } catch (_) {
          /* ignore */
        }
      } else {
        await AuthManager.saveUser(updatedUser);
      }
      
      setUser(updatedUser);
      setIsEditing(false);
      
      // Reload user data so form shows persisted values (Firestore/local + storage)
      await loadUserData();
      
      Alert.alert('Success', 'Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      Alert.alert('Error', 'Failed to update profile. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    if (user) {
      // Handle address - can be string or object
      let streetAddress = '';
      let city = '';
      let state = '';
      let zipcode = '';
      let country = 'USA';
      
      if (user.address) {
        if (typeof user.address === 'string') {
          streetAddress = user.address;
        } else if (typeof user.address === 'object') {
          // Extract separate address fields from object
          const addr = user.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
          streetAddress = addr.street || '';
          city = addr.city || '';
          state = addr.state || '';
          zipcode = addr.zipcode || '';
          country = addr.country || 'USA';
        }
      }
      
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: streetAddress,
        city: city,
        state: state,
        zipcode: zipcode,
        country: country,
      });
    }
    setIsEditing(false);
  };

  const handleDeleteAccountConfirm = async () => {
    if (isDeletingAccount) return;
    setIsDeletingAccount(true);
    try {
      await UnifiedAuthService.deleteAccount();
      setShowDeleteAccountModal(false);
      onLogout();
    } catch (error: any) {
      Alert.alert(
        'Could not delete account',
        error?.message || 'Something went wrong. Please try again or contact support.'
      );
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleSettingChange = async (key: string, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
    
    // Handle SMS opt-out/opt-in for A2P compliance
    if (key === 'smsReminders' && user?.phone) {
      try {
        const { smsService } = await import('../services/smsService');
        
        if (!value) {
          // User opted out - disable SMS reminders
          await smsService.optOut(user.phone);
          Alert.alert(
            'SMS Opted Out',
            'You have been unsubscribed from SMS reminders. You can opt back in anytime from Settings.'
          );
        } else {
          // User opted in - enable SMS reminders
          await smsService.optIn(user.phone);
          Alert.alert(
            'SMS Opted In',
            'You will now receive SMS appointment reminders.'
          );
        }
      } catch (error) {
        console.error('Error updating SMS opt-out status:', error);
        // Revert toggle on error
        setSettings(prev => ({
          ...prev,
          [key]: !value,
        }));
        Alert.alert(
          'Error',
          'Failed to update SMS preferences. Please try again.'
        );
      }
    }
  };

  const handleTabPress = (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile') => {
    // Home button should always go to dashboard, not chat
    if (onTabPress) {
      onTabPress(tab);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Loading profile...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { overflow: 'hidden' }]}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Profile</Text>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => setIsEditing(!isEditing)}
        >
          <Text style={styles.editButtonText}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={{ alignSelf: 'stretch', paddingBottom: 70 }}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        {/* Profile Information */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Profile Information</Text>
          
          <View style={styles.profileCard}>
            <View style={styles.avatarContainer}>
              <Text style={styles.avatarText}>
                {(user?.name || 'U').charAt(0).toUpperCase()}
              </Text>
            </View>
            
            <View style={styles.profileInfo}>
              <Text style={styles.profileName}>{user?.name || 'User'}</Text>
              <Text style={styles.profileEmail}>{user?.email || 'No email'}</Text>
              {currentTrial && currentTrial.isActive && !currentTrial.isExpired && (
                <View style={styles.trialBadge}>
                  <Text style={styles.trialBadgeText}>
                    🎉 Free Trial - {currentTrial.daysLeft} days left
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Editable Fields */}
          <View style={styles.formContainer}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Full Name</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={formData.name}
                onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                editable={isEditing}
                placeholder="Enter your full name"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={formData.email}
                onChangeText={(text) => setFormData(prev => ({ ...prev, email: text }))}
                editable={isEditing}
                placeholder="Enter your email"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Phone</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={formData.phone}
                onChangeText={(text) => setFormData(prev => ({ ...prev, phone: text }))}
                editable={isEditing}
                placeholder="Enter your phone number"
                keyboardType="phone-pad"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Street Address</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={formData.address}
                onChangeText={(text) => setFormData(prev => ({ ...prev, address: text }))}
                editable={isEditing}
                placeholder="Enter your street address"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>City</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={formData.city}
                onChangeText={(text) => setFormData(prev => ({ ...prev, city: text }))}
                editable={isEditing}
                placeholder="Enter your city"
                autoCapitalize="words"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Country</Text>
              {isEditing ? (
                <TouchableOpacity
                  style={[styles.input, styles.dropdownButton, !isEditing && styles.inputDisabled]}
                  onPress={() => setShowCountryDropdown(true)}
                  disabled={!isEditing}
                >
                  <Text style={[styles.dropdownButtonText, !formData.country && styles.placeholderText]}>
                    {formData.country 
                      ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country
                      : 'Select Country'}
                  </Text>
                  <Text style={styles.dropdownArrow}>▼</Text>
                </TouchableOpacity>
              ) : (
                <Text style={[styles.input, styles.inputDisabled]}>
                  {formData.country 
                    ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country
                    : 'Not set'}
                </Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>State/Province</Text>
              {isEditing ? (
                <TouchableOpacity
                  style={[styles.input, styles.dropdownButton, !isEditing && styles.inputDisabled]}
                  onPress={() => {
                    if (!formData.country) {
                      Alert.alert('Select Country First', 'Please select a country before choosing a state/province.');
                      return;
                    }
                    setShowStateDropdown(true);
                  }}
                  disabled={!isEditing}
                >
                  <Text style={[styles.dropdownButtonText, !formData.state && styles.placeholderText]}>
                    {formData.state || (formData.country === 'Canada' ? 'Select Province' : 'Select State')}
                  </Text>
                  <Text style={styles.dropdownArrow}>▼</Text>
                </TouchableOpacity>
              ) : (
                <Text style={[styles.input, styles.inputDisabled]}>
                  {formData.state || 'Not set'}
                </Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Zipcode</Text>
              <AppTextInput
                style={[styles.input, !isEditing && styles.inputDisabled, zipcodeError && styles.inputError]}
                value={formData.zipcode}
                onChangeText={(text) => {
                  setFormData(prev => ({ ...prev, zipcode: text }));
                  if (zipcodeError) setZipcodeError(null);
                }}
                editable={isEditing}
                placeholder="US: 12345 or Canada: A1A 1A1"
                autoCapitalize="characters"
                maxLength={7}
              />
              {zipcodeError ? <Text style={styles.errorText}>{zipcodeError}</Text> : null}
            </View>

            {isEditing && (
              <View style={styles.saveButtonContainer}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={handleCancelEdit}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.saveButton}
                  onPress={handleSaveProfile}
                >
                  <Text style={styles.saveButtonText}>Save Changes</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Credits Management */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Credits & Plans</Text>
          
          <View style={styles.settingsCard}>
            <TouchableOpacity 
              style={styles.settingItem}
              onPress={onNavigateToSubscription}
            >
              <View style={styles.settingLeft}>
                <Text style={styles.settingLabel}>Buy Credits</Text>
                <Text style={styles.settingDescription}>Purchase credits to book appointments</Text>
              </View>
              <Text style={styles.arrowIcon}>›</Text>
            </TouchableOpacity>

            {currentTrial && currentTrial.isActive && !currentTrial.isExpired && (
              <View style={styles.trialStatusCard}>
                <Text style={styles.trialStatusTitle}>🎉 Free Trial Active</Text>
                <Text style={styles.trialStatusText}>
                  {currentTrial.daysLeft} days remaining
                </Text>
                <Text style={styles.trialStatusDescription}>
                  Enjoying all Premium features! Your trial will auto-convert to Premium plan.
                </Text>
              </View>
            )}

            {currentTrial && currentTrial.isExpired && (
              <View style={styles.expiredTrialCard}>
                <Text style={styles.expiredTrialTitle}>⏰ Trial Expired</Text>
                <Text style={styles.expiredTrialText}>
                  Your free trial has ended. Choose a plan to continue.
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Settings - wrap in fixed-width container to prevent toggle overflow */}
        <View style={[styles.settingsSection, { width: WINDOW_WIDTH - 40, maxWidth: WINDOW_WIDTH - 40 }]}>
          <Text style={styles.settingsSectionTitle}>Settings</Text>

          <TouchableOpacity
            style={styles.userSettingItem}
            onPress={() => setShowVehiclesModal(true)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="My Vehicles"
          >
            <View style={styles.userSettingLeft}>
              <View style={styles.userSettingText}>
                <Text style={styles.userSettingTitle}>My Vehicles</Text>
                <Text style={styles.userSettingSubtitle}>Save a vehicle for faster booking</Text>
              </View>
            </View>
            <Text style={styles.arrowIcon}>›</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.userSettingItem}
            onPress={() => handleSettingChange('notifications', !settings.notifications)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityRole="switch"
            accessibilityState={{ checked: settings.notifications }}
            accessibilityLabel="Push Notifications"
          >
            <View style={styles.userSettingLeft}>
              <View style={styles.userSettingText}>
                <Text style={styles.userSettingTitle}>Push Notifications</Text>
                <Text style={styles.userSettingSubtitle}>Receive appointment reminders</Text>
              </View>
            </View>
            <View style={[styles.userToggle, settings.notifications && styles.userToggleActive]}>
              <View style={[styles.userToggleThumb, settings.notifications && styles.userToggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.userSettingItem}
            onPress={() => handleSettingChange('emailUpdates', !settings.emailUpdates)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityRole="switch"
            accessibilityState={{ checked: settings.emailUpdates }}
            accessibilityLabel="Email Updates"
          >
            <View style={styles.userSettingLeft}>
              <View style={styles.userSettingText}>
                <Text style={styles.userSettingTitle}>Email Updates</Text>
                <Text style={styles.userSettingSubtitle}>Receive updates via email</Text>
              </View>
            </View>
            <View style={[styles.userToggle, settings.emailUpdates && styles.userToggleActive]}>
              <View style={[styles.userToggleThumb, settings.emailUpdates && styles.userToggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.userSettingItem}
            onPress={() => handleSettingChange('smsReminders', !settings.smsReminders)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityRole="switch"
            accessibilityState={{ checked: settings.smsReminders }}
            accessibilityLabel="SMS Reminders"
          >
            <View style={styles.userSettingLeft}>
              <View style={styles.userSettingText}>
                <Text style={styles.userSettingTitle}>SMS Reminders</Text>
                <Text style={styles.userSettingSubtitle}>Receive SMS appointment reminders</Text>
              </View>
            </View>
            <View style={[styles.userToggle, settings.smsReminders && styles.userToggleActive]}>
              <View style={[styles.userToggleThumb, settings.smsReminders && styles.userToggleThumbActive]} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.userSettingItem}
            onPress={() => handleSettingChange('locationServices', !settings.locationServices)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityRole="switch"
            accessibilityState={{ checked: settings.locationServices }}
            accessibilityLabel="Location Services"
          >
            <View style={styles.userSettingLeft}>
              <View style={styles.userSettingText}>
                <Text style={styles.userSettingTitle}>Location Services</Text>
                <Text style={styles.userSettingSubtitle}>Find nearby businesses</Text>
              </View>
            </View>
            <View style={[styles.userToggle, settings.locationServices && styles.userToggleActive]}>
              <View style={[styles.userToggleThumb, settings.locationServices && styles.userToggleThumbActive]} />
            </View>
          </TouchableOpacity>
        </View>

        {/* Account Actions */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Account</Text>
          
          <View style={styles.actionsCard}>
            <TouchableOpacity 
              style={styles.actionItem}
              onPress={async () => {
                const url = 'https://bookapt.io/privacy.html';
                try {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } else {
                    const supported = await Linking.canOpenURL(url);
                    if (supported) {
                      await Linking.openURL(url);
                    } else {
                      Alert.alert('Error', 'Cannot open Privacy Policy link');
                    }
                  }
                } catch (error) {
                  Alert.alert('Error', 'Failed to open Privacy Policy');
                }
              }}
            >
              <Text style={styles.actionLabel}>Privacy Policy</Text>
              <Text style={styles.actionArrow}>›</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionItem}
              onPress={async () => {
                const url = 'https://bookapt.io/terms.html';
                try {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } else {
                    const supported = await Linking.canOpenURL(url);
                    if (supported) {
                      await Linking.openURL(url);
                    } else {
                      Alert.alert('Error', 'Cannot open Terms of Service link');
                    }
                  }
                } catch (error) {
                  Alert.alert('Error', 'Failed to open Terms of Service');
                }
              }}
            >
              <Text style={styles.actionLabel}>Terms of Service</Text>
              <Text style={styles.actionArrow}>›</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionItem}
              onPress={async () => {
                const url = 'https://orynsolutions.io/support';
                try {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } else {
                    const supported = await Linking.canOpenURL(url);
                    if (supported) {
                      await Linking.openURL(url);
                    } else {
                      Alert.alert('Error', 'Cannot open Help & Support link');
                    }
                  }
                } catch (error) {
                  Alert.alert('Error', 'Failed to open Help & Support');
                }
              }}
            >
              <Text style={styles.actionLabel}>Help & Support</Text>
              <Text style={styles.actionArrow}>›</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.actionItem}
              onPress={async () => {
                const email = 'info@orynsolutions.io';
                const url = `mailto:${email}`;
                try {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Contact Us', `Email: ${email}`);
                  }
                } catch (error) {
                  Alert.alert('Contact Us', `Email: ${email}`);
                }
              }}
            >
              <Text style={styles.actionLabel}>Contact Us</Text>
              <Text style={styles.actionArrow}>›</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.actionItem, styles.deleteAccountAction]}
              onPress={() => setShowDeleteAccountModal(true)}
              disabled={isDeletingAccount}
            >
              <Text style={styles.deleteAccountLabel}>Delete account</Text>
              <Text style={styles.actionArrow}>›</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <DeleteAccountConfirmModal
        visible={showDeleteAccountModal}
        onCancel={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccountConfirm}
        accountDeletionUrl="https://bookapt.io/privacy.html#account-deletion"
      />

      <MyVehiclesModal
        visible={showVehiclesModal}
        userId={user?.id || ''}
        selectedVehicleId={null}
        onSelectVehicleId={() => {
          // From profile settings, selection doesn't matter — we just manage vehicles.
          setShowVehiclesModal(false);
        }}
        onClose={() => setShowVehiclesModal(false)}
      />

      {/* Country Dropdown Modal */}
      <Modal
        visible={showCountryDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCountryDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCountryDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>Select Country</Text>
              <TouchableOpacity onPress={() => setShowCountryDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {COUNTRIES.map((country) => (
                <TouchableOpacity
                  key={country.code}
                  style={styles.dropdownOption}
                  onPress={() => {
                    setFormData(prev => {
                      const newData = { ...prev, country: country.code };
                      if (prev.country !== country.code) {
                        newData.state = '';
                      }
                      return newData;
                    });
                    setShowCountryDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownOptionText}>{country.name}</Text>
                  {formData.country === country.code && (
                    <Text style={styles.dropdownCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Dropdown Modal */}
      <Modal
        visible={showStateDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowStateDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>
                Select {formData.country === 'Canada' ? 'Province' : 'State'}
              </Text>
              <TouchableOpacity onPress={() => setShowStateDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {getStatesForCountry(formData.country).map((state) => (
                <TouchableOpacity
                  key={state.code}
                  style={styles.dropdownOption}
                  onPress={() => {
                    setFormData(prev => ({ ...prev, state: state.code }));
                    setShowStateDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownOptionText}>
                    {state.code} - {state.name}
                  </Text>
                  {formData.state === state.code && (
                    <Text style={styles.dropdownCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Bottom Menu */}
      {onTabPress && (
        <UserBottomMenu
          activeTab="profile"
          onTabPress={handleTabPress}
          onLogout={onLogout}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    width: WINDOW_WIDTH,
    maxWidth: WINDOW_WIDTH,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: '#007AFF',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  editButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  editButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
    padding: 20,
    width: WINDOW_WIDTH,
    maxWidth: WINDOW_WIDTH,
    overflow: 'hidden',
  },
  sectionContainer: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  profileCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  avatarContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  avatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  profileEmail: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  trialBadge: {
    backgroundColor: '#E8F5E8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  trialBadgeText: {
    fontSize: 12,
    color: '#2E7D32',
    fontWeight: '600',
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: 'white',
  },
  inputDisabled: {
    backgroundColor: '#F8F9FA',
    color: '#8E8E93',
  },
  inputError: {
    borderColor: '#FF3B30',
  },
  errorText: {
    fontSize: 13,
    color: '#FF3B30',
    marginTop: 6,
  },
  multilineInput: {
    height: 60,
    textAlignVertical: 'top',
  },
  saveButtonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  settingsCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    overflow: 'hidden',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
    width: '100%',
  },
  settingsSection: {
    marginBottom: 30,
    overflow: 'hidden',
    width: '100%',
  },
  settingsSectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  userSettingItem: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
    marginBottom: 10,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  userSettingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    marginRight: 12,
  },
  userSettingIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  userSettingText: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  userSettingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  userSettingSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  userToggle: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#e0e0e0',
    justifyContent: 'center',
    paddingHorizontal: 2,
    flexShrink: 0,
  },
  userToggleActive: {
    backgroundColor: '#007bff',
  },
  userToggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'white',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  userToggleThumbActive: {
    transform: [{ translateX: 20 }],
  },
  settingLeft: {
    flex: 1,
    minWidth: 0,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
  },
  arrowIcon: {
    fontSize: 20,
    color: '#C7C7CC',
    fontWeight: 'bold',
  },
  trialStatusCard: {
    backgroundColor: '#F0F8FF',
    borderRadius: 8,
    padding: 16,
    margin: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
  },
  trialStatusTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: 4,
  },
  trialStatusText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
    marginBottom: 4,
  },
  trialStatusDescription: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
  expiredTrialCard: {
    backgroundColor: '#FFF5F5',
    borderRadius: 8,
    padding: 16,
    margin: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#FF3B30',
  },
  expiredTrialTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FF3B30',
    marginBottom: 4,
  },
  expiredTrialText: {
    fontSize: 14,
    color: '#FF3B30',
    lineHeight: 16,
  },
  actionsCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
  },
  actionLabel: {
    fontSize: 16,
    color: '#1C1C1E',
  },
  actionArrow: {
    fontSize: 18,
    color: '#C7C7CC',
  },
  deleteAccountAction: {
    borderBottomWidth: 0,
    borderColor: '#ffebee',
    backgroundColor: '#fff5f5',
  },
  deleteAccountLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#c62828',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 12,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  placeholderText: {
    color: '#8E8E93',
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#8E8E93',
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  dropdownModal: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  dropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  dropdownTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  dropdownClose: {
    fontSize: 24,
    color: '#8E8E93',
    padding: 5,
  },
  dropdownList: {
    maxHeight: 400,
  },
  dropdownOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  dropdownCheckmark: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
    marginLeft: 10,
  },
});

export default UserProfileScreen;
